import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    faArrowRightToBracket,
    faBuilding,
    faHandHoldingBox,
    faMagicWandSparkles,
    faUser,
    faWarehouseFull,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useBreadcrumbs from 'use-react-router-breadcrumbs';

import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { SpacefillDebugActions } from '@spacefill/uikit/src/SpacefillDebugActions';
import { Breadcrumb } from '@spacefill/uikit/src/components/Breadcrumb';
import { Button } from '@spacefill/uikit/src/components/Button';

import { AnalyticsChatPanel } from '../pages/analytics-chat/AnalyticsChatPanel';
import { TopBarActionsAndNotifications } from './TopBarActionsAndNotifications';
import { TopBarProfile } from './TopBarProfile';
import { routes } from './TopbarRoutes';

const SpacefillTopbar = () => {
    const breadcrumbs = useBreadcrumbs(routes);
    const { user, switchRole } = useUserContext();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const { t } = useTranslation();
    const location = useLocation();
    const isCarrier = !!user.carrierId;

    const profileTopNavigation = user.multiRoles
        ? [
              {
                  title: t('Operator view'),
                  hasRoundedIcon: true,
                  icon: <FontAwesomeIcon icon={faWarehouseFull} />,
                  disabled: user.role === 'PROVIDER',
                  dataTestid: `nav-switch-operator${user.role === 'PROVIDER' ? '-disabled' : ''}`,
                  onClick: async () => await switchRole('PROVIDER'),
                  conditionForDisplay: () => !isCarrier,
              },
              {
                  title: t('Shipper view'),
                  hasRoundedIcon: true,
                  icon: <FontAwesomeIcon icon={faHandHoldingBox} />,
                  disabled: user.role === 'SHIPPER',
                  dataTestid: `nav-switch-shipper${user.role === 'SHIPPER' ? '-disabled' : ''}`,
                  onClick: async () => await switchRole('SHIPPER'),
                  conditionForDisplay: () => !isCarrier,
              },
          ]
        : [];
    const profileNavigation = [
        {
            title: t('Coworkers & Company'),
            hasRoundedIcon: true,
            icon: <FontAwesomeIcon icon={faBuilding} />,
            dataTestid: 'nav-company',
            isActive: location.pathname.startsWith('/company/coworkers'),
            to: '/company/coworkers',
            conditionForDisplay: () => !isCarrier,
        },
        {
            title: t('User profile'),
            hasRoundedIcon: true,
            icon: <FontAwesomeIcon icon={faUser} />,
            dataTestid: 'nav-profile',
            isActive: location.pathname.startsWith('/profile/'),
            to: '/profile/',
        },
        {
            title: t('Log out'),
            hasRoundedIcon: true,
            dataTestId: 'nav-logout',
            icon: <FontAwesomeIcon icon={faArrowRightToBracket} />,
            to: '/logout/',
        },
    ];
    return (
        <div className='flex items-center justify-between bg-gray-50 border-b border-gray-200 px-6 py-2 h-12'>
            <Breadcrumb breadcrumbs={breadcrumbs} />
            <div className='flex items-center gap-4'>
                {(user.isStaff || user.email?.endsWith('spacefill.fr')) && (
                    <div className='relative'>
                        <div className='pointer-events-none absolute -inset-[2px] overflow-hidden rounded-lg'>
                            <span className='absolute -inset-3 animate-spin bg-lime-500 [animation-duration:3.2s] motion-reduce:animate-none' />
                        </div>
                        <div className='relative rounded-md'>
                            <Button
                                variant='dark'
                                size='small'
                                iconStart={<FontAwesomeIcon icon={faMagicWandSparkles} />}
                                onClick={() => setIsChatOpen(true)}
                            >
                                {t('AI Insights')}
                            </Button>
                        </div>
                    </div>
                )}
                {(user.isStaff || user.email?.endsWith('spacefill.fr')) && (
                    <AnalyticsChatPanel
                        isOpen={isChatOpen}
                        onClose={() => setIsChatOpen(false)}
                    />
                )}
                {(user.isStaff || getEnvironmentVariable('featureTogglingEnabled')) && <SpacefillDebugActions />}
                <TopBarActionsAndNotifications />
                <TopBarProfile
                    profileTopNavigation={profileTopNavigation}
                    profileNavigation={profileNavigation}
                    userInfo={{
                        userName: `${user?.firstName} ${user?.lastName}`,
                        company: user?.customer?.company || user?.logisticProvider?.company || user?.carrier?.company,
                    }}
                />
            </div>
        </div>
    );
};

export { SpacefillTopbar };
